import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, ChefHat, User, Gift, LogOut, RefreshCw } from "lucide-react";
import PortalAuth from "@/components/portal/PortalAuth";
import PortalPendingScreen from "@/components/portal/PortalPendingScreen";
import PortalSettings from "@/components/portal/PortalSettings";
import GuestDetails from "@/components/portal/GuestDetails";
import { BADGE_COLORS } from "@/components/portal/badgeColors";
import { Award } from "lucide-react";

const RestaurantPortal = () => {
  const [account, setAccount] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestsError, setGuestsError] = useState(null);
  const [fullHistory, setFullHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchGuests = useCallback(async () => {
    setGuestsError(null);
    try {
      const response = await axios.get(`${API}/restaurant-portal/guests`, { withCredentials: true });
      setGuests(response.data);
    } catch (error) {
      console.warn("Error fetching guests:", error);
      setGuestsError(error.response?.data?.detail || "Gästeliste konnte nicht geladen werden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurant-portal/me`, { withCredentials: true });
      setAccount(response.data);
      fetchGuests();
    } catch (error) {
      setLoading(false);
    }
  }, [fetchGuests]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = async (email, password) => {
    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API}/restaurant-portal/login`,
        { email, password },
        { withCredentials: true }
      );
      setAccount(response.data.account);
      toast.success("Willkommen zurück!");
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleRegister = async (registerData) => {
    if (!registerData.email || !registerData.password || !registerData.restaurant_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API}/restaurant-portal/register`, registerData, { withCredentials: true });
      setAccount(response.data.account);
      toast.success("Registrierung eingegangen! Ihr Konto wird nach Prüfung freigeschaltet.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/restaurant-portal/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.warn("Logout error:", error);
    }
    setAccount(null);
    setGuests([]);
  };

  const loadFullHistory = async () => {
    if (!selectedGuest) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/restaurant-portal/guest/${selectedGuest.user.user_id}`, { withCredentials: true });
      setFullHistory(response.data.visits || []);
    } catch (error) {
      toast.error("Historie konnte nicht geladen werden");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedGuest) return;
    setSendingMessage(true);
    try {
      await axios.post(`${API}/restaurant-portal/message`,
        { user_id: selectedGuest.user.user_id, message: messageText.trim() },
        { withCredentials: true }
      );
      toast.success("Nachricht an den Gast gesendet!");
      setMessageText("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nachricht konnte nicht gesendet werden");
    } finally {
      setSendingMessage(false);
    }
  };

  if (!account && !loading) {
    return <PortalAuth onLogin={handleLogin} onRegister={handleRegister} isLoggingIn={isLoggingIn} />;
  }

  if (account && !account.verified) {
    return (
      <PortalPendingScreen
        account={account}
        onRefresh={() => { setLoading(true); checkAuth(); }}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="restaurant-portal-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-primary" />
                <div>
                  <span className="font-semibold block leading-tight">{account?.restaurant_name || "Restaurant Portal"}</span>
                  <span className="text-xs text-muted-foreground leading-tight" data-testid="portal-account-email">{account?.email}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-primary fill-primary" />
                <span className="font-semibold gradient-text">Étoile</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground" data-testid="portal-logout-button">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">Gäste-Vorlieben</h1>
          <p className="text-muted-foreground">
            Diese Gäste haben ihre Vorlieben für Restaurants freigegeben – bereiten Sie ein persönliches Erlebnis vor
          </p>
        </div>

        {/* Info Banner */}
        <Card className="bg-primary/10 border-primary/20 mb-8" data-testid="info-banner">
          <CardContent className="p-6 flex items-center gap-4">
            <Gift className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <p className="font-semibold text-primary mb-1">Ihre Chance für perfekten Service</p>
              <p className="text-sm text-muted-foreground">
                Sie sehen hier Vorlieben, Unverträglichkeiten und die Sterne-Historie Ihrer Gäste.
                Überraschen Sie sie z.B. mit einem Aperitif, dem Lieblingsdessert oder einem Gruß aus der Küche –
                und bedanken Sie sich nach dem Besuch direkt per Nachricht.
              </p>
              {guests.length > 0 && guests.every(g => !g.visits_to_own_restaurant) && (
                <p className="text-xs text-muted-foreground mt-2 border-t border-primary/20 pt-2" data-testid="no-restaurant-match-hint">
                  Hinweis: Ihr Restaurantname „{account?.restaurant_name}" konnte keinem Michelin-Restaurant zugeordnet werden –
                  die Stammgast-Anzeige ist daher nicht verfügbar. Der Name muss dem offiziellen Michelin-Eintrag entsprechen.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <PortalSettings account={account} onAccountUpdate={setAccount} />

        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!loading && guestsError && (
          <Card className="bg-destructive/5 border-destructive/30" data-testid="guests-error">
            <CardContent className="py-12 text-center">
              <User className="w-12 h-12 text-destructive mx-auto mb-4" />
              <p className="text-destructive font-medium mb-4">{guestsError}</p>
              <Button variant="outline" onClick={() => { setLoading(true); fetchGuests(); }} data-testid="guests-retry-button">
                <RefreshCw className="w-4 h-4 mr-2" />Erneut versuchen
              </Button>
            </CardContent>
          </Card>
        )}
        {!loading && !guestsError && guests.length === 0 && (
          <Card className="bg-card border-border" data-testid="guests-empty-state">
            <CardContent className="py-12 text-center max-w-xl mx-auto">
              <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-foreground font-medium mb-2">Noch keine Gäste haben ihre Vorlieben freigegeben</p>
              <p className="text-sm text-muted-foreground mb-4">
                Gäste erscheinen hier, sobald sie in ihrem Étoile-Profil unter
                <span className="font-medium text-foreground"> Profil → Datenschutz </span>
                die Option <span className="font-medium text-foreground">„Vorlieben mit Restaurants teilen"</span> aktiviert haben.
              </p>
              <p className="text-xs text-muted-foreground border-t border-border pt-3">
                Hinweis: Testdaten aus der Vorschau-Umgebung sind in der Produktivumgebung nicht vorhanden –
                jede Umgebung hat eine eigene Datenbank.
              </p>
            </CardContent>
          </Card>
        )}
        {!loading && !guestsError && guests.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Guest List */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Gäste ({guests.length})</h2>
              <div className="space-y-3">
                {guests.map((guest, index) => (
                  <Card
                    key={guest.user.user_id}
                    className={`bg-card border-border cursor-pointer card-hover ${
                      selectedGuest?.user.user_id === guest.user.user_id ? 'border-primary' : ''
                    }`}
                    onClick={() => { setSelectedGuest(guest); setFullHistory(null); }}
                    data-testid={`guest-card-${index}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {guest.user.picture ? (
                          <img src={guest.user.picture} alt="" className="w-12 h-12 rounded-full" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                            <User className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{guest.user.name}</p>
                          <div className="flex items-center gap-2 text-sm flex-wrap">
                            <span className={BADGE_COLORS[guest.user.badge_tier]}>
                              {guest.user.badge_tier}
                            </span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Star className="w-3 h-3 text-primary fill-primary" />
                              {guest.user.total_stars} stars
                            </span>
                          </div>
                          {guest.visits_to_own_restaurant?.count > 0 && (
                            <span
                              className="inline-flex items-center gap-1 mt-1.5 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 border border-amber-500/30 font-medium"
                              data-testid={`regular-guest-badge-${index}`}
                            >
                              <Award className="w-3 h-3" />
                              {guest.visits_to_own_restaurant.count >= 2
                                ? `Stammgast · ${guest.visits_to_own_restaurant.count} Besuche bei Ihnen`
                                : "1 Besuch bei Ihnen"}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Guest Details */}
            <div>
              <GuestDetails
                guest={selectedGuest}
                loyaltyProgram={account?.loyalty_program}
                messageText={messageText}
                setMessageText={setMessageText}
                sendingMessage={sendingMessage}
                onSendMessage={handleSendMessage}
                fullHistory={fullHistory}
                loadingHistory={loadingHistory}
                onToggleHistory={() => fullHistory ? setFullHistory(null) : loadFullHistory()}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RestaurantPortal;
